import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, MessageSquare, Megaphone, Sparkles, AlertTriangle, Eye } from "lucide-react";
import { PageHeader, PageBody } from "../components/PageScaffold";
import { Panel, CenteredSpinner, ErrorBanner, EmptyState, Pill } from "../components/primitives";
import { Sparkline } from "../components/Sparkline";
import { AnalyticsApi } from "../lib/api";
import { qk } from "../lib/query";
import { cn } from "../lib/cn";
import type { AnalyticsWindow, AnalyticsOverview } from "../lib/types";

const WINDOWS: { id: AnalyticsWindow; label: string }[] = [
  { id: "24h", label: "24h" },
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
];

function fmtDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 100) / 10;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

function StatTile({ icon: Icon, label, value, series, stroke, fill }: {
  icon: typeof BarChart3; label: string; value: number; series?: number[]; stroke?: string; fill?: string;
}) {
  return (
    <Panel className="p-3 flex flex-col gap-1.5 min-w-0">
      <div className="flex items-center gap-1.5 text-fg-subtle">
        <Icon className="size-3.5 flex-shrink-0" />
        <span className="text-[11px] uppercase tracking-wide truncate">{label}</span>
      </div>
      <div className="text-2xl font-semibold text-fg leading-none tabular-nums">{value.toLocaleString()}</div>
      <div className="h-6">
        {series && series.some((v) => v > 0)
          ? <Sparkline values={series} strokeClassName={stroke} fillClassName={fill} />
          : <div className="h-full" />}
      </div>
    </Panel>
  );
}

/** Total-activity-per-bucket bars with sparse axis labels (first / mid / last). */
function ActivityBars({ data }: { data: AnalyticsOverview["timeline"] }) {
  const totals = data.buckets.map((_, i) => data.replies[i] + data.proactive[i] + data.spawns[i] + data.errors[i]);
  const max = Math.max(...totals, 1);
  const n = data.buckets.length;
  const tickIdx = new Set([0, Math.floor(n / 2), n - 1]);
  return (
    <div>
      <div className="flex items-end gap-[2px] h-32">
        {totals.map((t, i) => (
          <div key={i} className="flex-1 min-w-0 h-full flex flex-col justify-end group relative" title={`${data.buckets[i]}: ${t}`}>
            <div
              className={cn("w-full rounded-sm", t > 0 ? "bg-accent/70 group-hover:bg-accent" : "bg-surface-2")}
              style={{ height: `${Math.max((t / max) * 100, t > 0 ? 4 : 1.5)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-1.5 text-[10px] text-fg-subtle">
        {data.buckets.map((b, i) => (tickIdx.has(i) ? <span key={i}>{b}</span> : null)).filter(Boolean)}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [win, setWin] = useState<AnalyticsWindow>("24h");
  const { data, isLoading, error } = useQuery({
    queryKey: qk.analytics.overview(win),
    queryFn: () => AnalyticsApi.overview(win),
    refetchInterval: 30_000,
  });

  const toggle = (
    <div className="inline-flex rounded-md border border-border overflow-hidden">
      {WINDOWS.map((w) => (
        <button
          key={w.id}
          onClick={() => setWin(w.id)}
          className={cn(
            "px-2.5 h-7 text-xs font-medium transition-colors",
            win === w.id ? "bg-accent text-white" : "bg-surface text-fg-muted hover:bg-surface-2",
          )}
        >
          {w.label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <PageHeader title="Analytics" icon={BarChart3} subtitle="Fleet activity & trends" actions={toggle} />
      <PageBody className="space-y-4">
        {isLoading && <CenteredSpinner label="crunching the numbers" />}
        {error && <ErrorBanner error={error as Error} />}
        {data && (
          <>
            {/* Counter tiles */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatTile icon={MessageSquare} label="Replies" value={data.counters.replies} series={data.timeline.replies} stroke="stroke-accent" fill="fill-accent/15" />
              <StatTile icon={Megaphone} label="Proactive" value={data.counters.proactive} series={data.timeline.proactive} stroke="stroke-info" fill="fill-info/15" />
              <StatTile icon={Sparkles} label="Spawns" value={data.counters.spawns} series={data.timeline.spawns} stroke="stroke-fg-muted" fill="fill-fg-muted/10" />
              <StatTile icon={AlertTriangle} label="Errors" value={data.counters.errors} series={data.timeline.errors} stroke="stroke-danger" fill="fill-danger/15" />
              <StatTile icon={Eye} label="Lurked" value={data.counters.lurks} />
            </div>

            {/* Activity over time + spawn health */}
            <div className="grid md:grid-cols-3 gap-4">
              <Panel className="p-4 md:col-span-2 min-w-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-fg">Activity over time</h3>
                  <span className="text-[11px] text-fg-subtle">{data.total_events.toLocaleString()} events · {win}</span>
                </div>
                {data.total_events === 0
                  ? <EmptyState icon={<BarChart3 className="size-7" />} title="No activity yet" description="Nothing happened in this window." />
                  : <ActivityBars data={data.timeline} />}
              </Panel>

              <Panel className="p-4 min-w-0">
                <h3 className="text-sm font-semibold text-fg mb-3">Spawn health</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-semibold text-fg tabular-nums">{data.spawnHealth.success_rate}%</span>
                  <span className="text-xs text-fg-subtle">success rate</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {data.spawnHealth.completed > 0 && <Pill tone="success">{data.spawnHealth.completed} done</Pill>}
                  {data.spawnHealth.failed > 0 && <Pill tone="danger">{data.spawnHealth.failed} failed</Pill>}
                  {data.spawnHealth.killed > 0 && <Pill tone="warning">{data.spawnHealth.killed} killed</Pill>}
                  {data.spawnHealth.running > 0 && <Pill tone="accent">{data.spawnHealth.running} running</Pill>}
                  {data.spawnHealth.total === 0 && <span className="text-xs text-fg-subtle">no spawns</span>}
                </div>
                <div className="mt-3 pt-3 border-t border-border flex justify-between text-xs">
                  <span className="text-fg-subtle">avg duration</span>
                  <span className="text-fg tabular-nums">{fmtDuration(data.spawnHealth.avg_duration_ms)}</span>
                </div>
              </Panel>
            </div>

            {/* By persona + event mix */}
            <div className="grid md:grid-cols-3 gap-4">
              <Panel className="p-4 md:col-span-2 min-w-0">
                <h3 className="text-sm font-semibold text-fg mb-3">By persona</h3>
                {data.byPersona.length === 0
                  ? <p className="text-xs text-fg-subtle">No persona activity in this window.</p>
                  : (
                    <div className="overflow-x-auto -mx-1">
                      <table className="w-full text-sm min-w-[420px]">
                        <thead>
                          <tr className="text-[11px] uppercase tracking-wide text-fg-subtle text-right">
                            <th className="text-left font-medium pb-1.5 px-1">Persona</th>
                            <th className="font-medium pb-1.5 px-1">Replies</th>
                            <th className="font-medium pb-1.5 px-1">Proactive</th>
                            <th className="font-medium pb-1.5 px-1">Lurked</th>
                            <th className="font-medium pb-1.5 px-1">Spawns</th>
                            <th className="font-medium pb-1.5 px-1">Errors</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.byPersona.map((p) => (
                            <tr key={p.persona} className="border-t border-border/60 text-right tabular-nums">
                              <td className="text-left py-1.5 px-1 font-medium text-fg">{p.persona}</td>
                              <td className="px-1 text-fg-muted">{p.replies || "—"}</td>
                              <td className="px-1 text-fg-muted">{p.proactive || "—"}</td>
                              <td className="px-1 text-fg-muted">{p.lurks || "—"}</td>
                              <td className="px-1 text-fg-muted">{p.spawns || "—"}</td>
                              <td className={cn("px-1", p.errors ? "text-danger" : "text-fg-muted")}>{p.errors || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </Panel>

              <Panel className="p-4 min-w-0">
                <h3 className="text-sm font-semibold text-fg mb-3">Event mix</h3>
                {data.byKind.length === 0
                  ? <p className="text-xs text-fg-subtle">No events.</p>
                  : (
                    <div className="space-y-1.5">
                      {data.byKind.slice(0, 10).map((k) => {
                        const pct = Math.round((k.count / data.byKind[0].count) * 100);
                        return (
                          <div key={k.kind} className="flex items-center gap-2 text-xs">
                            <span className="w-28 truncate text-fg-muted" title={k.kind}>{k.kind}</span>
                            <div className="flex-1 h-2 rounded-full bg-surface-2 overflow-hidden">
                              <div className="h-full rounded-full bg-accent/60" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="w-8 text-right tabular-nums text-fg">{k.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </Panel>
            </div>
          </>
        )}
      </PageBody>
    </>
  );
}
